<template>
  <div class="floating-audio-bubble">
    <!-- Unified Bubble that morphs between states -->
    <Bubble
      :expanded="isExpanded"
      :expanded-width="320"
      :collapsed-size="collapsedWidth"
      :collapsed-height="48"
      id="audio-bubble"
      origin="top-right"
      :class="{ 'cursor-pointer': !isExpanded }"
      @click="handleBubbleClick"
    >
      <!-- Collapsed State Content -->
      <div
        v-if="!isExpanded"
        class="h-full flex items-center justify-center gap-3 px-3"
      >
        <!-- Simple icon when nothing playing -->
        <template v-if="!hasAudioPlaying">
          <Volume2 class="h-5 w-5 text-primary" />
        </template>
        
        <!-- Mini player when audio is playing -->
        <template v-else>
          <!-- Album Art or Icon -->
          <div class="flex-shrink-0">
            <img
              v-if="currentTrackImage"
              :src="currentTrackImage"
              class="h-8 w-8  object-cover"
              alt="Album art"
            />
            <div v-else class="h-8 w-8 bg-primary/20 flex items-center justify-center">
              <Music2 v-if="spotifyStore.isPlaying" class="h-4 w-4 text-green-500" />
              <Music v-else class="h-4 w-4 text-primary" />
            </div>
          </div>
          
          <!-- Track Info (only when wide enough) -->
          <div v-if="hasAudioPlaying" class="flex-1 min-w-0 max-w-[120px]">
            <div class="text-xs font-medium truncate">
              {{ currentTrackName || 'Playing...' }}
            </div>
            <div v-if="currentTrackArtist" class="text-[10px] text-muted-foreground truncate">
              {{ currentTrackArtist }}
            </div>
          </div>

          <!-- Play/Pause button -->
          <Button
            variant="ghost"
            size="icon"
            class="h-7 w-7 flex-shrink-0"
            @click.stop="togglePlayPause"
          >
            <Pause v-if="isAnyPlaying" class="h-4 w-4" />
            <Play v-else class="h-4 w-4" />
          </Button>
        </template>
      </div>

      <!-- Expanded State Content -->
      <template v-if="isExpanded">
        <!-- Header -->
        <BubbleHeader
          title="Audio"
          @close="isExpanded = false"
        >
          <template #icon>
            <Volume2 class="h-4 w-4 text-primary" />
          </template>
        </BubbleHeader>

        <!-- Content -->
        <BubbleContent>
          <!-- Spotify Section - show when connected -->
          <div v-if="spotifyStore.isConnected" class="space-y-3">
            <!-- Current Track / Playback State -->
            <BubbleItem
              v-if="spotifyStore.isPlaying || spotifyStore.currentTrack"
              variant="spotify"
              :title="currentTrackName || 'Spotify'"
              :subtitle="currentTrackArtist"
            >
              <template #icon>
                <img
                  v-if="currentTrackImage"
                  :src="currentTrackImage"
                  class="h-full w-full object-cover"
                  alt="Album art"
                />
                <Music2 v-else class="h-5 w-5" />
              </template>
            </BubbleItem>
            
            <!-- No active playback state -->
            <BubbleCard v-else variant="spotify" class="flex items-center gap-3">
              <div class="h-10 w-10 bg-green-900/30 flex items-center justify-center flex-shrink-0">
                <Music2 class="h-5 w-5 text-green-500/50" />
              </div>
              <div class="flex-1 min-w-0">
                <div class="text-sm font-medium text-muted-foreground">No active Spotify session</div>
                <div class="text-xs text-muted-foreground/60">Start playback from a Spotify node</div>
              </div>
            </BubbleCard>
            
            <!-- Spotify Controls (only show when there's playback) -->
            <div v-if="spotifyStore.isPlaying || spotifyStore.currentTrack" class="flex items-center justify-center gap-2 py-1">
              <Button variant="ghost" size="icon" @click="audioStore.previousTrack()">
                <SkipBack class="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" class="h-10 w-10" @click="toggleSpotifyPlayback">
                <Pause v-if="spotifyStore.isPlaying" class="h-5 w-5" />
                <Play v-else class="h-5 w-5" />
              </Button>
              <Button variant="ghost" size="icon" @click="audioStore.nextTrack()">
                <SkipForward class="h-4 w-4" />
              </Button>
            </div>
            
            <!-- Spotify Volume Control -->
            <div v-if="(spotifyStore.isPlaying || spotifyStore.currentTrack) && spotifyStore.playbackState?.device?.supports_volume" class="flex items-center gap-2 px-1">
              <Button
                variant="ghost"
                size="icon"
                class="h-6 w-6"
                @click="toggleSpotifyMute"
              >
                <VolumeX v-if="isSpotifyMuted" class="h-3.5 w-3.5 text-muted-foreground/60" />
                <Volume2 v-else class="h-3.5 w-3.5" />
              </Button>
              <Slider
                :model-value="[isSpotifyMuted ? 0 : spotifyVolume]"
                :max="100"
                :step="1"
                :disabled="isSpotifyMuted"
                class="flex-1"
                @update:model-value="(val) => val && handleSpotifyVolumeChange(val)"
              />
              <span class="text-xs text-muted-foreground w-8 text-right">
                {{ isSpotifyMuted ? 0 : spotifyVolume }}%
              </span>
            </div>
          </div>

          <!-- Music Items -->
          <div v-if="musicItems.length > 0" class="space-y-2">
            <div class="text-xs font-medium text-muted-foreground/60 uppercase tracking-wide">
              Music
            </div>
            <TransitionGroup name="music-item" tag="div" class="space-y-2 relative">
              <BubbleItem
                v-for="item in musicItems"
                :key="item.id"
                variant="music"
                :title="item.name"
              >
                <template #icon>
                  <img 
                    v-if="getMusicNodeCover(item.id)"
                    :src="getMusicNodeCover(item.id)!"
                    class="h-full w-full object-cover"
                    alt="Album cover"
                  />
                  <Music v-else class="h-5 w-5" />
                </template>
                <template #meta>
                  <div v-if="item.loop" class="flex items-center gap-1 text-xs text-muted-foreground/60">
                    <Repeat class="h-3 w-3" />
                    <span>Loop</span>
                  </div>
                </template>
                <template #actions>
                  <Button
                    variant="ghost"
                    size="icon"
                    @click="audioStore.stopMusic(item.id)"
                  >
                    <X class="h-4 w-4" />
                  </Button>
                </template>
              </BubbleItem>
            </TransitionGroup>
          </div>

          <!-- Display Music (from remote displays) -->
          <div v-if="displayMusicItems.length > 0" class="space-y-3">
            <div
              v-for="[displayId, items] in displayMusicByDisplay"
              :key="displayId"
              class="space-y-2"
            >
              <!-- Display header with master fader -->
              <div class="flex items-center gap-2">
                <div class="text-xs text-muted-foreground flex items-center gap-1 flex-shrink-0">
                  <Monitor class="h-3 w-3" />
                  {{ getDisplayName(displayId) }}
                </div>
                <div class="flex-1 flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    class="h-5 w-5"
                    @click="toggleDisplayMasterMute(displayId)"
                    :title="isDisplayMasterMuted(displayId) ? 'Unmute Display' : 'Mute Display'"
                  >
                    <VolumeX v-if="isDisplayMasterMuted(displayId)" class="h-3 w-3 text-muted-foreground/60" />
                    <Volume2 v-else class="h-3 w-3" />
                  </Button>
                  <Slider
                    :model-value="[isDisplayMasterMuted(displayId) ? 0 : getDisplayMasterVolume(displayId)]"
                    :max="1"
                    :step="0.01"
                    :disabled="isDisplayMasterMuted(displayId)"
                    class="flex-1"
                    @update:model-value="(val) => val && handleMasterVolumeChange(displayId, val)"
                  />
                  <span class="text-[10px] text-muted-foreground w-7 text-right">
                    {{ Math.round((isDisplayMasterMuted(displayId) ? 0 : getDisplayMasterVolume(displayId)) * 100) }}%
                  </span>
                </div>
              </div>
              <TransitionGroup name="music-item" tag="div" class="space-y-2 relative">
                <BubbleCard
                  v-for="item in items"
                  :key="item.nodeId"
                  :variant="item.playlistId ? 'default' : 'music'"
                  class="flex flex-col gap-2"
                >
                  <!-- Track Info Row -->
                  <div class="flex items-center gap-3">
                    <div 
                      class="h-8 w-8 flex items-center justify-center flex-shrink-0 overflow-hidden"
                      :class="item.playlistId ? 'bg-amber-900/40' : 'bg-blue-900/40'"
                    >
                      <!-- Cover art if available -->
                      <img 
                        v-if="getMusicNodeCover(item.nodeId)"
                        :src="getMusicNodeCover(item.nodeId)!"
                        class="h-full w-full object-cover"
                        alt="Album cover"
                      />
                      <!-- Fallback icon -->
                      <ListMusic v-else-if="item.playlistId" class="h-4 w-4 text-amber-400" />
                      <Music v-else class="h-4 w-4 text-blue-400" />
                    </div>
                    <div class="flex-1 min-w-0">
                      <div class="text-sm font-medium text-foreground truncate">{{ item.name }}</div>
                      <div class="flex items-center gap-2 text-xs text-muted-foreground/60">
                        <!-- Playlist info -->
                        <span v-if="item.playlistName" class="truncate">{{ item.playlistName }}</span>
                        <span v-if="item.playlistId && item.totalTracks" class="flex-shrink-0">
                          {{ (item.trackIndex || 0) + 1 }}/{{ item.totalTracks }}
                        </span>
                        <!-- Loop indicator for non-playlist tracks -->
                        <div v-else-if="item.loop" class="flex items-center gap-1">
                          <Repeat class="h-3 w-3" />
                          <span>Loop</span>
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      @click="stopDisplayMusic(displayId, item.nodeId)"
                    >
                      <X class="h-4 w-4" />
                    </Button>
                  </div>
                  
                  <!-- Playlist Controls (only for playlist tracks) -->
                  <div v-if="item.playlistId" class="flex items-center justify-center gap-1 py-1">
                    <Button 
                      variant="ghost" 
                      size="icon"
                      class="h-7 w-7"
                      :class="{ 'text-amber-400': item.shuffle }"
                      @click="togglePlaylistShuffle(displayId, item)"
                      :title="item.shuffle ? 'Shuffle On' : 'Shuffle Off'"
                    >
                      <Shuffle class="h-3.5 w-3.5" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      class="h-7 w-7"
                      @click="playlistPrev(displayId, item)"
                      title="Previous Track"
                    >
                      <SkipBack class="h-3.5 w-3.5" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      class="h-8 w-8"
                      @click="togglePlaylistPause(displayId)"
                      :title="getDisplayPauseState(displayId) ? 'Resume' : 'Pause'"
                    >
                      <Play v-if="getDisplayPauseState(displayId)" class="h-4 w-4" />
                      <Pause v-else class="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      class="h-7 w-7"
                      @click="playlistNext(displayId, item)"
                      title="Next Track"
                    >
                      <SkipForward class="h-3.5 w-3.5" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      class="h-7 w-7"
                      :class="{ 'text-amber-400': item.repeatMode !== 'none' }"
                      @click="cyclePlaylistRepeat(displayId, item)"
                      :title="`Repeat: ${item.repeatMode || 'none'}`"
                    >
                      <Repeat1 v-if="item.repeatMode === 'one'" class="h-3.5 w-3.5" />
                      <Repeat v-else class="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  
                  <!-- Volume Controls -->
                  <div class="flex items-center gap-2 px-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      class="h-6 w-6"
                      @click="toggleNodeMute(displayId, item.nodeId, getNodeVolume(item.nodeId))"
                    >
                      <VolumeX v-if="isNodeMuted(item.nodeId)" class="h-3.5 w-3.5 text-muted-foreground/60" />
                      <Volume2 v-else class="h-3.5 w-3.5" />
                    </Button>
                    <Slider
                      :model-value="[isNodeMuted(item.nodeId) ? 0 : getNodeVolume(item.nodeId)]"
                      :max="1"
                      :step="0.01"
                      :disabled="isNodeMuted(item.nodeId)"
                      class="flex-1"
                      @update:model-value="(val) => val && handleVolumeChange(displayId, item.nodeId, val)"
                    />
                    <span class="text-xs text-muted-foreground w-8 text-right">
                      {{ Math.round((isNodeMuted(item.nodeId) ? 0 : getNodeVolume(item.nodeId)) * 100) }}%
                    </span>
                  </div>
                </BubbleCard>
              </TransitionGroup>
            </div>
          </div>

          <!-- Connect Spotify when not connected and nothing playing -->
          <div v-if="!spotifyStore.isConnected && !hasAudioPlaying" class="text-center py-4">
            <div class="text-sm text-muted-foreground mb-3">No audio playing</div>
            <Button variant="secondary" @click="openSpotifySettings">
              <Music2 class="h-4 w-4 mr-2 text-green-500" />
              Connect Spotify
            </Button>
          </div>
          
          <!-- No audio playing but Spotify is connected (handled above in Spotify section) -->
          <div v-else-if="!hasAudioPlaying && !spotifyStore.isConnected" class="text-center py-4">
            <div class="text-sm text-muted-foreground">No audio playing</div>
          </div>
        </BubbleContent>

        <!-- Footer: Stop All -->
        <BubbleFooter v-if="hasAudioPlaying">
          <Button
            class="w-full gold-button"
            @click="stopAllAudio"
          >
            <Square class="h-3 w-3 mr-1.5 fill-current" />
            Stop All Audio
          </Button>
        </BubbleFooter>
      </template>
    </Bubble>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, TransitionGroup } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import { useAudioPlaybackStore } from '@/stores/audioPlayback'
import { useDisplayAudioStore } from '@/stores/displayAudio'
import { useDisplaysStore } from '@/stores/displays'
import { useSpotifyStore } from '@/stores/spotify'
import { useMusicNodesStore } from '@/stores/musicNodes'
import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import {
  Bubble,
  BubbleHeader,
  BubbleContent,
  BubbleFooter,
  BubbleItem,
  BubbleCard
} from '@/components/ui/bubble'
import {
  Volume2,
  VolumeX,
  Square,
  Music,
  Music2,
  Play,
  Pause,
  SkipBack,
  SkipForward,
  X,
  Repeat,
  Repeat1,
  Shuffle,
  ListMusic,
  Monitor
} from 'lucide-vue-next'

const audioStore = useAudioPlaybackStore()
const displayAudioStore = useDisplayAudioStore()
const displaysStore = useDisplaysStore()
const spotifyStore = useSpotifyStore()
const musicNodesStore = useMusicNodesStore()

// Check if any audio is playing (needed early for collapsed width calculation)
const hasAudioPlaying = computed(() => {
  return audioStore.hasPlayingAudio || spotifyStore.isPlaying || displayAudioStore.allDisplayMusic.length > 0
})

// Collapsed width depends on whether audio is playing (mini player is wider)
const collapsedWidth = computed(() => hasAudioPlaying.value ? 220 : 48)

const isExpanded = ref(false)

// Handle bubble click - only expand when collapsed
const handleBubbleClick = () => {
  if (!isExpanded.value) {
    isExpanded.value = true
  }
}

// Track volume per node (local state for immediate UI feedback during drag)
const localVolumes = ref<Map<string, number>>(new Map())
const mutedNodes = ref<Set<string>>(new Set())

// Track master volume per display (local state for immediate UI feedback)
const localMasterVolumes = ref<Map<string, number>>(new Map())
const mutedDisplays = ref<Set<string>>(new Set())
const previousMasterVolumes = ref<Map<string, number>>(new Map())

// Get master volume for a display
const getDisplayMasterVolume = (displayId: string): number => {
  // First check local state (for immediate feedback during drag)
  const localVol = localMasterVolumes.value.get(displayId)
  if (localVol !== undefined) {
    return localVol
  }
  // Then use the display play state
  const state = displayAudioStore.displayPlayStates.get(displayId)
  return state?.masterVolume ?? 1
}

// Check if a display is muted
const isDisplayMasterMuted = (displayId: string): boolean => {
  return mutedDisplays.value.has(displayId)
}

// Toggle master mute for a display
const toggleDisplayMasterMute = async (displayId: string) => {
  const currentVol = getDisplayMasterVolume(displayId)
  
  if (mutedDisplays.value.has(displayId)) {
    // Unmute - restore previous volume
    mutedDisplays.value.delete(displayId)
    mutedDisplays.value = new Set(mutedDisplays.value)
    const restoreVol = previousMasterVolumes.value.get(displayId) ?? 1
    localMasterVolumes.value.set(displayId, restoreVol)
    localMasterVolumes.value = new Map(localMasterVolumes.value)
    await displayAudioStore.sendCommandToDisplay(displayId, {
      type: 'set-master-volume',
      masterVolume: restoreVol
    })
  } else {
    // Mute - save current volume and set to 0
    previousMasterVolumes.value.set(displayId, currentVol)
    mutedDisplays.value.add(displayId)
    mutedDisplays.value = new Set(mutedDisplays.value)
    localMasterVolumes.value.set(displayId, 0)
    localMasterVolumes.value = new Map(localMasterVolumes.value)
    await displayAudioStore.sendCommandToDisplay(displayId, {
      type: 'set-master-volume',
      masterVolume: 0
    })
  }
}

// Debounced function to persist master volume
const debouncedUpdateMasterVolume = useDebounceFn(async (displayId: string, volume: number) => {
  // Master volume is not persisted to DB, only sent to presenter
}, 300)

// Handle master volume change
const handleMasterVolumeChange = async (displayId: string, value: number[]) => {
  const volume = value[0]
  
  // Update local state immediately for responsive UI
  localMasterVolumes.value.set(displayId, volume)
  localMasterVolumes.value = new Map(localMasterVolumes.value)
  
  // If currently muted, unmute
  if (mutedDisplays.value.has(displayId)) {
    mutedDisplays.value.delete(displayId)
    mutedDisplays.value = new Set(mutedDisplays.value)
  }
  
  // Send command to presenter
  await displayAudioStore.sendCommandToDisplay(displayId, {
    type: 'set-master-volume',
    masterVolume: volume
  })
}

// Get the displayed volume for a node - use store as source of truth
const getNodeVolume = (nodeId: string): number => {
  // First check local state (for immediate feedback during drag)
  const localVol = localVolumes.value.get(nodeId)
  if (localVol !== undefined) {
    return localVol
  }
  // Then use the music node store as the source of truth
  const node = musicNodesStore.getMusicNodeById(nodeId)
  return node?.volume ?? 1
}

// Check if a node is muted
const isNodeMuted = (nodeId: string): boolean => {
  return mutedNodes.value.has(nodeId)
}

// Toggle mute for a specific node
const toggleNodeMute = async (displayId: string, nodeId: string, currentVolume: number) => {
  if (mutedNodes.value.has(nodeId)) {
    // Unmute - restore previous volume
    mutedNodes.value.delete(nodeId)
    mutedNodes.value = new Set(mutedNodes.value)
    // Send command to presenter with restored volume
    await displayAudioStore.sendCommandToDisplay(displayId, {
      type: 'set-volume',
      nodeId,
      volume: currentVolume
    })
  } else {
    // Mute
    mutedNodes.value.add(nodeId)
    mutedNodes.value = new Set(mutedNodes.value)
    // Send command to presenter with 0 volume
    await displayAudioStore.sendCommandToDisplay(displayId, {
      type: 'set-volume',
      nodeId,
      volume: 0
    })
  }
}

// Debounced function to update volume in the store (persists to DB)
const debouncedUpdateVolume = useDebounceFn(async (nodeId: string, volume: number) => {
  await musicNodesStore.updateMusicNode(nodeId, { volume })
}, 300)

// Handle volume change - update locally immediately, debounce the DB update and command
const handleVolumeChange = async (displayId: string, nodeId: string, value: number[]) => {
  const volume = value[0]
  
  // Update local state immediately for responsive UI
  localVolumes.value.set(nodeId, volume)
  localVolumes.value = new Map(localVolumes.value)
  
  // If currently muted, unmute
  if (mutedNodes.value.has(nodeId)) {
    mutedNodes.value.delete(nodeId)
    mutedNodes.value = new Set(mutedNodes.value)
  }
  
  // Send command to presenter immediately for real-time audio feedback
  await displayAudioStore.sendCommandToDisplay(displayId, {
    type: 'set-volume',
    nodeId,
    volume
  })
  
  // Debounce the database update
  debouncedUpdateVolume(nodeId, volume)
}

// Spotify volume state
const isSpotifyMuted = ref(false)
const spotifyVolumeBeforeMute = ref(50)

// Get current Spotify volume from playback state
const spotifyVolume = computed(() => {
  return spotifyStore.playbackState?.device?.volume_percent ?? 50
})

// Toggle Spotify mute
const toggleSpotifyMute = async () => {
  if (isSpotifyMuted.value) {
    // Unmute - restore previous volume
    isSpotifyMuted.value = false
    await spotifyStore.setVolume(spotifyVolumeBeforeMute.value)
  } else {
    // Mute - save current volume and set to 0
    spotifyVolumeBeforeMute.value = spotifyVolume.value
    isSpotifyMuted.value = true
    await spotifyStore.setVolume(0)
  }
}

// Debounced Spotify volume update
const debouncedSpotifyVolume = useDebounceFn(async (volume: number) => {
  await spotifyStore.setVolume(volume)
}, 100)

// Handle Spotify volume change
const handleSpotifyVolumeChange = (value: number[]) => {
  const volume = value[0]
  
  // If muted, unmute
  if (isSpotifyMuted.value) {
    isSpotifyMuted.value = false
  }
  
  // Debounce the API call
  debouncedSpotifyVolume(volume)
}

// Clear local volume override when store updates (so we pick up the new value)
watch(() => musicNodesStore.allMusicNodes, () => {
  // Clear local overrides so getNodeVolume reads from store
  localVolumes.value.clear()
}, { deep: true })

// Subscribe to display audio broadcasts
const subscribeToDisplays = async () => {
  console.log('🔊 FloatingAudioBubble: Subscribing to displays, count:', displaysStore.allDisplays.length)
  for (const display of displaysStore.allDisplays) {
    console.log('🔊 FloatingAudioBubble: Subscribing to display:', display.id, display.name)
    await displayAudioStore.subscribeToDisplay(display.id)
  }
}

// Watch for display changes
watch(() => displaysStore.allDisplays, async (displays) => {
  for (const display of displays) {
    await displayAudioStore.subscribeToDisplay(display.id)
  }
}, { deep: true })

onMounted(async () => {
  await subscribeToDisplays()
})

onUnmounted(async () => {
  await displayAudioStore.cleanupEditor()
})

const isAnyPlaying = computed(() => {
  return spotifyStore.isPlaying || audioStore.playingMusicItems.size > 0
})

const musicItems = computed(() => {
  return Array.from(audioStore.playingMusicItems.values())
})

// Display music computed properties
const displayMusicItems = computed(() => {
  return displayAudioStore.allDisplayMusic
})

const displayMusicByDisplay = computed(() => {
  return Array.from(displayAudioStore.musicByDisplay.entries())
})

// Get cover URL for a music node (for uploaded MP3s)
const getMusicNodeCover = (nodeId: string): string | undefined => {
  const node = musicNodesStore.getMusicNodeById(nodeId)
  return node?.coverUrl
}

const getDisplayName = (displayId: string): string => {
  const display = displaysStore.allDisplays.find(d => d.id === displayId)
  return display?.name || 'Unknown Display'
}

const getDisplayPauseState = (displayId: string): boolean => {
  const state = displayAudioStore.displayPlayStates.get(displayId)
  return state?.isPaused ?? false
}

// Track items being removed (for disabling interactions during animation)
const removingItems = ref<Set<string>>(new Set())

const stopDisplayMusic = async (displayId: string, nodeId: string) => {
  // Mark as removing to disable further interactions
  removingItems.value.add(nodeId)
  removingItems.value = new Set(removingItems.value)
  
  await displayAudioStore.sendCommandToDisplay(displayId, {
    type: 'stop-music',
    nodeId
  })
}

// Playlist control functions
const togglePlaylistPause = async (displayId: string) => {
  const displayState = displayAudioStore.displayPlayStates.get(displayId)
  const isPaused = displayState?.isPaused ?? false
  
  await displayAudioStore.sendCommandToDisplay(displayId, {
    type: isPaused ? 'playlist-resume' : 'playlist-pause'
  })
}

const playlistPrev = async (displayId: string, item: any) => {
  await displayAudioStore.sendCommandToDisplay(displayId, {
    type: 'playlist-prev',
    playlistId: item.playlistId,
    nodeId: item.nodeId
  })
}

const playlistNext = async (displayId: string, item: any) => {
  await displayAudioStore.sendCommandToDisplay(displayId, {
    type: 'playlist-next',
    playlistId: item.playlistId,
    nodeId: item.nodeId
  })
}

const togglePlaylistShuffle = async (displayId: string, item: any) => {
  await displayAudioStore.sendCommandToDisplay(displayId, {
    type: 'playlist-shuffle',
    shuffle: !item.shuffle,
    playlistId: item.playlistId
  })
}

const cyclePlaylistRepeat = async (displayId: string, item: any) => {
  // Cycle through: none -> all -> one -> none
  const modes: Array<'none' | 'all' | 'one'> = ['none', 'all', 'one']
  const currentIndex = modes.indexOf(item.repeatMode || 'none')
  const nextMode = modes[(currentIndex + 1) % modes.length]
  
  await displayAudioStore.sendCommandToDisplay(displayId, {
    type: 'playlist-repeat',
    repeatMode: nextMode,
    playlistId: item.playlistId
  })
}

// Clear removing state when store updates
watch(() => displayAudioStore.allDisplayMusic, () => {
  removingItems.value.clear()
}, { deep: true })

const currentTrackName = computed(() => {
  // Priority: Spotify > display music > local music
  if (spotifyStore.currentTrack?.name) return spotifyStore.currentTrack.name
  if (displayMusicItems.value.length > 0) return displayMusicItems.value[0].name
  return musicItems.value[0]?.name
})

const currentTrackArtist = computed(() => {
  return spotifyStore.currentTrack?.artists?.map((a: { name: string }) => a.name).join(', ')
})

const currentTrackImage = computed(() => {
  // Priority: Spotify album art > uploaded music cover art
  if (spotifyStore.currentTrack?.album?.images?.[0]?.url) {
    return spotifyStore.currentTrack.album.images[0].url
  }
  // Check display music for cover art
  if (displayMusicItems.value.length > 0) {
    const nodeId = displayMusicItems.value[0].nodeId
    const coverUrl = getMusicNodeCover(nodeId)
    if (coverUrl) return coverUrl
  }
  // Check local music for cover art
  if (musicItems.value.length > 0) {
    const nodeId = musicItems.value[0].id
    const node = musicNodesStore.getMusicNodeById(nodeId)
    if (node?.coverUrl) return node.coverUrl
  }
  return undefined
})

const togglePlayPause = async () => {
  if (spotifyStore.isPlaying) {
    await audioStore.pauseSpotify()
  } else if (audioStore.playingSpotifyItem) {
    await spotifyStore.play()
  }
}

const toggleSpotifyPlayback = async () => {
  if (spotifyStore.isPlaying) {
    await audioStore.pauseSpotify()
  } else {
    await spotifyStore.play()
  }
}

const stopAllAudio = async () => {
  await audioStore.stopAll()
}

const openSpotifySettings = async () => {
  // Connect to Spotify if not connected, otherwise the settings are in the settings modal
  if (!spotifyStore.isConnected) {
    await spotifyStore.connect()
  }
}
</script>

<style scoped>
/* Music item list animations */
.music-item-enter-active,
.music-item-leave-active {
  transition: all 0.3s ease;
}

.music-item-leave-active {
  position: absolute;
  width: 100%;
}

.music-item-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.music-item-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

/* Smooth movement of other items when one is added/removed */
.music-item-move {
  transition: transform 0.3s ease;
}

/* Gold button styling */
.gold-button {
  background: var(--gold-gradient);
  color: #1a1a1a;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  font-size: 0.875rem;
  border: none;
  box-shadow: 
    0 2px 8px rgba(212, 175, 55, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  transition: all 200ms ease;
}

.gold-button:hover:not(:disabled) {
  box-shadow: 
    0 4px 12px rgba(212, 175, 55, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  transform: translateY(-1px);
}

.gold-button:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 
    0 1px 4px rgba(212, 175, 55, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
}
</style>
